/**
 * Hooks de gestion des Polls - Architecture Unifiée
 *
 * ATTENTION : Ces hooks sont CRITIQUES pour l'architecture.
 * NE MODIFIEZ PAS les types de retour ou la logique de transformation
 * sans autorisation explicite de Julien.
 *
 * Éléments protégés :
 * - Types de retour UnifiedPoll (pas de retour vers Poll legacy)
 * - Transformation des données dans createPoll/updatePoll
 * - Cache TanStack Query et invalidation
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { pollService } from "../services/PollService";
import type { UnifiedPoll } from "../services/PollService";
import { supabase } from "../lib/supabase";

// ========================================
// TYPES
// ========================================

// NOTE : Poll legacy gardé pour compatibilité uniquement
import { Poll } from "../lib/pollStorage"; // LEGACY - à migrer
import type {
  PollCreateInput,
  PollUpdateInput,
} from "../lib/pollStorageUnified";

export interface UsePollOptions {
  type?: "date" | "form" | "quizz" | "availability";
  enabled?: boolean;
  staleTime?: number;
  gcTime?: number;
}

export interface UsePollsOptions extends UsePollOptions {
  limit?: number;
  offset?: number;
  orderBy?: "created_at" | "updated_at" | "title";
  order?: "asc" | "desc";
}

// ========================================
// ADAPTATEURS POUR LA TRANSITION
// ========================================

// Adapter pour transformer Poll (ancien format) vers UnifiedPoll (nouveau format)
//
// ⚠️ CODE MORT — NE PAS UTILISER POUR LES FORMULAIRES (audit 2026-06-19).
// Cette fonction n'est appelée par AUCUN chemin runtime : seul son propre test
// unitaire (usePoll.adaptLegacy.test.ts) l'importe. Elle est conservée — et non
// supprimée — uniquement pour ne pas casser ce test.
//
// PIÈGE : pour les form-polls, elle lit les questions depuis `poll_data`
// (ligne `questions: legacyPoll.poll_data?.questions`), alors que la
// persistance réelle écrit dans `poll_data_json` (cf. PollService.createPoll →
// poll_data_json, et la lecture prod via PollService.getPoll → mergePollRow qui
// remonte poll_data_json au top-level). Un futur dev qui rebrancherait cet
// adaptateur sur le flux de vote ferait apparaître des options/questions vides
// (→ fallback « Option » / « Option 1/2 ») car il lirait la mauvaise colonne.
//
// Le vrai chemin de lecture des formulaires est PollService.getPoll /
// getPollBySlug (colonne poll_data_json), PAS cet adaptateur. Si on rebranche
// un jour ce code, corriger d'abord `poll_data` → `poll_data_json`.
export const adaptLegacyPollToUnified = (legacyPoll: Poll): UnifiedPoll => {
  const pd = (legacyPoll.poll_data ?? {}) as Record<string, unknown>;
  // Form polls: check poll_data_json for config params (displayMode, themeId, etc.)
  // Fall back to poll_data for legacy polls
  const pollDataJson =
    (legacyPoll.poll_data_json as Record<string, unknown>) ?? {};
  return {
    ...legacyPoll,
    // Transformer les données spécifiques depuis poll_data vers le niveau supérieur
    selectedDates: legacyPoll.poll_data?.selectedDates,
    questions: legacyPoll.poll_data?.questions,
    timeSlots: legacyPoll.poll_data?.timeSlots,
    dateGroups: legacyPoll.poll_data?.dateGroups,
    showTimeSlots: legacyPoll.poll_data?.showTimeSlots,
    notificationsEnabled: legacyPoll.poll_data?.notificationsEnabled,
    participantEmails: legacyPoll.poll_data?.participantEmails,
    parsedAvailabilities: legacyPoll.poll_data?.parsedAvailabilities,
    proposedSlots: legacyPoll.poll_data?.proposedSlots,
    settings: legacyPoll.poll_data?.settings,
    // Form-polls : remonter les champs stockés dans poll_data_json au top-level,
    // sinon FormPollVote (poll.displayMode) / FormPollResults lisent undefined.
    displayMode: pollDataJson.displayMode ?? pd.displayMode,
    themeId: pollDataJson.themeId ?? pd.themeId,
    resultsVisibility: pollDataJson.resultsVisibility ?? pd.resultsVisibility,
    conditionalRules: pollDataJson.conditionalRules ?? pd.conditionalRules,
    // Conserver les nouveaux champs invités
    creator_type: legacyPoll.creator_type,
    guest_fingerprint: legacyPoll.guest_fingerprint,
  } as UnifiedPoll;
};

// Plus besoin d'adaptUnifiedToLegacy - on utilise UnifiedPoll directement

export function usePoll(id: string, options: UsePollOptions = {}) {
  const {
    enabled = true,
    staleTime = 5 * 60 * 1000,
    gcTime = 10 * 60 * 1000,
  } = options;

  return useQuery({
    queryKey: ["poll", id],
    queryFn: async () => {
      const poll = await pollService.getPoll(id);
      return poll;
    },
    enabled: enabled && !!id,
    staleTime,
    gcTime,
    select: (data: UnifiedPoll) => data, // Pas de transformation, on utilise UnifiedPoll directement
  });
}

export function usePolls(options: UsePollsOptions = {}) {
  const {
    type,
    enabled = true,
    limit = 50,
    offset = 0,
    orderBy = "created_at",
    order = "desc",
    staleTime = 2 * 60 * 1000,
    gcTime = 5 * 60 * 1000,
  } = options;

  return useQuery({
    queryKey: ["polls", { type, limit, offset, orderBy, order }],
    queryFn: async () => {
      let query = supabase
        .from("polls")
        .select("*")
        .range(offset, offset + limit - 1)
        .order(orderBy, { ascending: order === "asc" });

      if (type) {
        query = query.eq("type", type);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data as Poll[];
    },
    enabled,
    staleTime,
    gcTime,
  });
}

/**
 * Hook de création de Poll - CRITIQUE
 *
 * ATTENTION : La transformation vers UnifiedPoll doit TOUJOURS
 * préserver la logique de fusion intelligente.
 * NE MODIFIEZ PAS sans autorisation explicite de Julien.
 */
export function useCreatePoll() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (poll: PollCreateInput) => {
      // Bug hunt 2026-04-23 — etape B : via RPC create_poll (SECURITY DEFINER).
      const p = poll as unknown as Record<string, unknown>;
      const { getGuestOwnerToken } =
        await import("@core/lib/guestIdentityService");
      const { data, error } = await supabase.rpc("create_poll", {
        p_type: p.type as string,
        p_title: p.title as string,
        p_slug: p.slug as string,
        p_poll_data_json: (p.poll_data_json as Record<string, unknown>) ?? {},
        p_description: (p.description as string | null) ?? null,
        p_creator_id: (p.creator_id as string | null) ?? null,
        p_creator_type: (p.creator_type as string) ?? "user",
        p_guest_fingerprint: (p.guest_fingerprint as string | null) ?? null,
        p_guest_session_id: (p.guest_session_id as string | null) ?? null,
        p_status: (p.status as string) ?? "draft",
        p_guest_owner_token: getGuestOwnerToken(),
      });

      if (error || !data) throw error ?? new Error("create_poll returned null");

      // Transformer vers UnifiedPoll
      const pollData = data as any;
      const pollDataJson = pollData.poll_data_json || {};

      return {
        ...pollDataJson,
        id: pollData.id,
        title: pollData.title || pollDataJson.title,
        slug: pollData.slug || pollDataJson.slug,
        type: pollData.type || pollDataJson.type,
        status: pollData.status || pollDataJson.status,
        creator_type: pollData.creator_type || pollDataJson.creator_type,
        creator_id: pollData.creator_id || pollDataJson.creator_id,
        guest_fingerprint:
          pollData.guest_fingerprint || pollDataJson.guest_fingerprint,
        created_at: pollData.created_at,
        updated_at: pollData.updated_at,
      } as UnifiedPoll;
    },
    onSuccess: (newPoll) => {
      // Invalider les caches pertinents
      queryClient.invalidateQueries({ queryKey: ["polls"] });
      queryClient.setQueryData(["poll", newPoll.id], newPoll);
    },
    onError: (error) => {
      console.error("Error creating poll:", error);
    },
  });
}

export function useUpdatePoll() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      updates,
    }: {
      id: string;
      updates: PollUpdateInput;
    }) => {
      const { data, error } = await supabase
        .from("polls")
        .update({ ...updates, updated_at: new Date().toISOString() })
        .eq("id", id)
        .select()
        .single();

      if (error) throw error;

      // Transformer vers UnifiedPoll
      const pollData = data as any;
      const pollDataJson = pollData.poll_data_json || {};

      return {
        ...pollDataJson,
        id: pollData.id,
        title: pollData.title || pollDataJson.title,
        slug: pollData.slug || pollDataJson.slug,
        type: pollData.type || pollDataJson.type,
        status: pollData.status || pollDataJson.status,
        creator_type: pollData.creator_type || pollDataJson.creator_type,
        creator_id: pollData.creator_id || pollDataJson.creator_id,
        guest_fingerprint:
          pollData.guest_fingerprint || pollDataJson.guest_fingerprint,
        created_at: pollData.created_at,
        updated_at: pollData.updated_at,
      } as UnifiedPoll;
    },
    onSuccess: (updatedPoll) => {
      // Mettre à jour les caches
      queryClient.setQueryData(["poll", updatedPoll.id], updatedPoll);
      queryClient.invalidateQueries({ queryKey: ["polls"] });
    },
    onError: (error) => {
      console.error("Error updating poll:", error);
    },
  });
}

export function useDeletePoll() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("polls").delete().eq("id", id);

      if (error) throw error;
      return id;
    },
    onSuccess: (deletedId) => {
      // Nettoyer les caches
      queryClient.removeQueries({ queryKey: ["poll", deletedId] });
      queryClient.invalidateQueries({ queryKey: ["polls"] });
    },
    onError: (error) => {
      console.error("Error deleting poll:", error);
    },
  });
}

// ========================================
// HOOKS SPÉCIALISÉS (Backward Compatibility)
// ========================================

export function useDatePolls(options = {}) {
  return usePolls({ ...options, type: "date" });
}

export function useFormPolls(options = {}) {
  return usePolls({ ...options, type: "form" });
}

export function useQuizzPolls(options = {}) {
  return usePolls({ ...options, type: "quizz" });
}

export function useAvailabilityPolls(options = {}) {
  return usePolls({ ...options, type: "availability" });
}

export function useDatePoll(
  id: string,
  options: Omit<UsePollOptions, "type"> = {},
) {
  return usePoll(id, { ...options, type: "date" });
}

export function useFormPoll(
  id: string,
  options: Omit<UsePollOptions, "type"> = {},
) {
  return usePoll(id, { ...options, type: "form" });
}

export function useQuizz(
  id: string,
  options: Omit<UsePollOptions, "type"> = {},
) {
  return usePoll(id, { ...options, type: "quizz" });
}

export function useAvailability(
  id: string,
  options: Omit<UsePollOptions, "type"> = {},
) {
  return usePoll(id, { ...options, type: "availability" });
}

// ========================================
// HOOKS UTILITAIRES
// ========================================

export function usePollBySlug(
  slug?: string,
  options: { enabled?: boolean; staleTime?: number; gcTime?: number } = {},
) {
  return useQuery({
    queryKey: ["poll", "slug", slug],
    queryFn: async () => {
      if (!slug) return null;

      const poll = await pollService.getPollBySlug(slug);
      return poll;
    },
    enabled: !!slug && options.enabled !== false,
    staleTime: options.staleTime || 2 * 60 * 1000,
    gcTime: options.gcTime || 5 * 60 * 1000,
    select: (data: UnifiedPoll) => data, // Pas de transformation, on utilise UnifiedPoll directement
  });
}

// ========================================
// HOOKS DE CRÉATION SPÉCIALISÉS
// ========================================

export function useCreateDatePoll() {
  const createPoll = useCreatePoll();

  return {
    ...createPoll,
    createPoll: (pollData: Omit<PollCreateInput, "type">) =>
      createPoll.mutateAsync({ ...pollData, type: "date" }),
  };
}

export function useCreateFormPoll() {
  const createPoll = useCreatePoll();

  return {
    ...createPoll,
    createPoll: (pollData: Omit<PollCreateInput, "type">) =>
      createPoll.mutateAsync({ ...pollData, type: "form" }),
  };
}

export function useCreateQuizz() {
  const createPoll = useCreatePoll();

  return {
    ...createPoll,
    createPoll: (pollData: Omit<PollCreateInput, "type">) =>
      createPoll.mutateAsync({ ...pollData, type: "quizz" }),
  };
}

export function useCreateAvailabilityPoll() {
  const createPoll = useCreatePoll();

  return {
    ...createPoll,
    createPoll: (pollData: Omit<PollCreateInput, "type">) =>
      createPoll.mutateAsync({ ...pollData, type: "availability" }),
  };
}

// ========================================
// UTILITAIRES
// ========================================

export function useInvalidatePolls() {
  const queryClient = useQueryClient();

  return () => {
    queryClient.invalidateQueries({ queryKey: ["polls"] });
  };
}

export function usePrefetchPolls(options: UsePollsOptions = {}) {
  const queryClient = useQueryClient();

  return (polls: Poll[]) => {
    // Optimized with for...of for better performance
    for (const poll of polls) {
      queryClient.prefetchQuery({
        queryKey: ["poll", poll.id],
        queryFn: async () => poll,
        staleTime: options.staleTime || 5 * 60 * 1000,
      });
    }
  };
}
